import {Button} from "primereact/button";
import {Dialog} from "primereact/dialog";
import {Dropdown} from "primereact/dropdown";
import {InputTextarea} from "primereact/inputtextarea";
import {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";

const orderCancellationReasons = [
    "FinancingNotMaterializedPartner",
    "CompletionNotAchievedPartner",
    "MainOrderScopeSufficientPartner",
    "IncorrectlyCommissionedPartner",
    "AccidentallyEnteredTwiceByClientPartner",
    "DeadlineExceededPartner",
    "InspectionByClientPartner",
];

/**
 * Callback executed when the deletion is performed.
 * @param deleteOrders flag, if Value Orders should also be deleted/canceled
 * @param reason reason for Value order deletion (only relevant, if deleteOrders=true)
 * @param comment comment for Value order deletion (only relevant, if deleteOrders=true)
 */
export type OnDeleteValuationCallback = (deleteOrders: boolean, reason?: string, comment?: string) => void;

interface DeleteValuationDialogProps {
    /** revaId to delete (the dialog will be shown, once this value is set) */
    revaId: string | null;
    /** if this is true, the option to also cancel the value order(s) is displayed */
    hasValueOrders: boolean;
    /** callback to execute when the deletion is performed */
    onDelete: OnDeleteValuationCallback;
    /** callback to execute when the dialog is closed without deleting */
    onCancel: () => void;
}

/**
 * Dialog to confirm the deletion of a REVA valuation.
 * If the valuation also has Value Orders attached to it, these can also be canceled with a provided reason.
 * The dialog will be shown, once a revaId to be deleted os set.
 */
export function DeleteValuationDialog({revaId, hasValueOrders, onDelete, onCancel}: DeleteValuationDialogProps) {
    const [comment, setComment] = useState<string | null>(null);
    const [reason, setReason] = useState<string | null>(null);
    const [deleting, setDeleting] = useState(false);
    const {t} = useTranslation();

    const handleHide = () => {
        onCancel();
        setComment(null);
        setReason(null);
    };

    const handleDelete = (deleteOrders: boolean, reason?: string, comment?: string) => {
        setDeleting(true);
        onDelete(deleteOrders, reason, comment);
    };

    const header = <span className="font-semibold">{t("deleteDialog.title", {revaId: revaId})}</span>;
    const cancelButton = <Button label={t("deleteDialog.cancel")} className="p-button-text" onClick={handleHide} />;
    const deleteRevaButton = (
        <Button label={t("deleteDialog.deleteRevaOnly")} className="ml-2" onClick={() => handleDelete(false)} />
    );
    const deleteAllButton = (
        <Button
            label={t("deleteDialog.deleteWithOrder")}
            className="ml-2"
            severity="danger"
            onClick={() => handleDelete(true, reason ?? undefined, comment ?? undefined)}
            disabled={!comment || !reason}
        />
    );

    const dialogVisible = !!revaId;

    useEffect(() => {
        if (revaId == null) {
            setDeleting(false);
        }
    }, [revaId]);

    const revaOnlyFooter = (
        <div>
            {cancelButton}
            {deleteRevaButton}
        </div>
    );

    const footer = (
        <div className="grid grid-cols-12 gap-4">
            <div className="col-span-9">
                {cancelButton}
                {deleteAllButton}
            </div>
            <div className="col-span-3">{deleteRevaButton}</div>
        </div>
    );

    if (deleting) {
        return (
            <Dialog visible={dialogVisible} onHide={handleHide} header={header} showCloseIcon={false}>
                <div className="flex justify-center items-center w-full">
                    <div className="pi pi-spin pi-spinner text-6xl" />
                </div>
            </Dialog>
        );
    }
    if (!hasValueOrders)
        return (
            <Dialog
                visible={dialogVisible}
                onHide={handleHide}
                footer={revaOnlyFooter}
                header={header}
                showCloseIcon={false}
            />
        );

    return (
        <Dialog visible={dialogVisible} onHide={handleHide} footer={footer} header={header} showCloseIcon={false}>
            <div className="grid grid-cols-12 gap-4">
                <div className="col-span-9 rounded-border bg-surface-100 dark:bg-surface-700 p-4">
                    <span>{t("deleteDialog.labelWithOrder")}</span>
                    <div className="grid grid-cols-12 gap-4">
                        <div className="col-span-3 mt-4 flex flex-col">
                            <span className="font-bold text-lg my-4">{t("deleteDialog.reason")}</span>
                            <span className="font-bold text-lg mt-4">{t("deleteDialog.comment")}</span>
                        </div>
                        <div className="col-span-9 mt-4">
                            <Dropdown
                                value={reason}
                                options={orderCancellationReasons}
                                onChange={(e) => setReason(e.value)}
                                className="w-full mt-2"
                                placeholder={t("deleteDialog.placeholder")}
                            />
                            <InputTextarea
                                className="w-full mt-2"
                                value={comment || ""}
                                tabIndex={1}
                                rows={5}
                                onChange={(e) => setComment(e.target.value || null)}
                            />
                        </div>
                    </div>
                </div>
                <div className="col-span-3 text-center">{t("deleteDialog.labelRevaOnly")}</div>
            </div>
        </Dialog>
    );
}
